import type { Quote, QuoteDetails, QuoteFollowUpEntry, QuoteItem } from '../types';
import { DEFAULT_QUOTE_SECTIONS } from '../types';
import { calculateQuoteTotals, computePosNumbers } from './quoteLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THREE-WAY MERGE FOR A QUOTE SAVE THAT MISSED ITS ANCHOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `public.quotes` keeps every position in ONE jsonb column (`items`) and every
 * text/section/meta field in another (`details`). A save writes those columns
 * whole. Two tabs editing the same quote therefore overwrote each other: tab A
 * adds five positions, tab B (still on the old state) changes one price and
 * saves — A's five positions are gone, and nothing reported it.
 *
 * `updateQuote` now anchors the write on `updated_at`. When the anchor misses,
 * this module replays the LOCAL CHANGES (local vs base) onto the server state
 * instead of writing the stale whole:
 *
 *   base   — the state the editor loaded / last saved successfully,
 *   local  — the patch the editor wants to write (Partial<Quote>),
 *   server — what is in the row now.
 *
 * Rules:
 *  - Scalar columns: a key counts as changed only if `local` carries it AND it
 *    differs from `base`. Changed keys win, everything else stays at `server`.
 *  - `items`, keyed by position `id`:
 *      added locally        → inserted after its local predecessor,
 *      deleted locally      → removed (even if the other side edited it),
 *      edited locally       → the fields local changed are laid onto the
 *                             server version of the position; fields local
 *                             did NOT change keep the server value,
 *      added on the server  → kept,
 *      deleted on the server but edited locally → kept (an edit is intent).
 *    Order: server order, unless ONLY local reordered — then local order.
 *    The other side's additions go in after their predecessor in that side's
 *    list; additions behind its last shared position are appended at the end.
 *    Position numbers and the net/gross totals are recomputed from the merged
 *    list, since both depend on the whole list.
 *  - `details`, per top-level key: local changed keys win, all other keys come
 *    from the server. `followUpLog` is always the union (the editor never
 *    removes a follow-up entry; the follow-up dialog writes them elsewhere).
 *
 * Pure: no I/O, no clock. The result is a PATCH to be written onto `server`.
 */

/**
 * Key-order independent JSON: jsonb hands objects back with its own key order
 * (by length, then alphabetically), so a plain JSON.stringify would call an
 * untouched position "changed" merely because it came back from the server.
 */
const stable = (v: unknown): string => {
  if (v === undefined) return 'undefined';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  const rec = v as Record<string, unknown>;
  return `{${Object.keys(rec)
    .filter((k) => rec[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stable(rec[k])}`)
    .join(',')}}`;
};

const same = (a: unknown, b: unknown): boolean => stable(a) === stable(b);

const followUpKey = (e: QuoteFollowUpEntry): string => `${e.at}|${e.by ?? ''}`;

/** Union of two follow-up logs: server entries first, then unknown local ones, oldest first. */
export const unionFollowUpLog = (
  server: QuoteFollowUpEntry[] | undefined,
  local: QuoteFollowUpEntry[] | undefined,
): QuoteFollowUpEntry[] => {
  const out = [...(server || [])];
  const seen = new Set(out.map(followUpKey));
  for (const e of local || []) {
    const k = followUpKey(e);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out.sort((a, b) => String(a.at).localeCompare(String(b.at)));
};

/** Did list X change the relative order of the ids it shares with base? */
const reordered = (base: QuoteItem[], other: QuoteItem[]): boolean => {
  const baseIds = new Set(base.map((i) => i.id));
  const otherIds = new Set(other.map((i) => i.id));
  const inBase = base.filter((i) => otherIds.has(i.id)).map((i) => i.id);
  const inOther = other.filter((i) => baseIds.has(i.id)).map((i) => i.id);
  return !same(inBase, inOther);
};

/**
 * Keys whose value differs between two versions of the same position.
 * `posNumber` is derived from the whole list (computePosNumbers) and shifts on
 * every insert above a position — it is no edit and is recomputed after the merge.
 */
const changedFields = (before: QuoteItem, after: QuoteItem): (keyof QuoteItem)[] => {
  const keys = new Set<keyof QuoteItem>([
    ...(Object.keys(before) as (keyof QuoteItem)[]),
    ...(Object.keys(after) as (keyof QuoteItem)[]),
  ]);
  return [...keys].filter((k) => k !== 'id' && k !== 'posNumber' && !same(before[k], after[k]));
};

export const mergeQuoteItems = (
  base: QuoteItem[],
  local: QuoteItem[],
  server: QuoteItem[],
): QuoteItem[] => {
  const baseById = new Map(base.map((i) => [i.id, i]));
  const localById = new Map(local.map((i) => [i.id, i]));
  const serverById = new Map(server.map((i) => [i.id, i]));

  const resolved = new Map<string, QuoteItem>();

  for (const s of server) {
    const b = baseById.get(s.id);
    const l = localById.get(s.id);
    if (b && !l) continue; // deleted locally
    if (!l) { resolved.set(s.id, s); continue; } // server-only addition
    if (!b) { resolved.set(s.id, { ...s, ...l }); continue; } // same id new on both sides
    const fields = changedFields(b, l);
    if (fields.length === 0) { resolved.set(s.id, s); continue; }
    const merged: QuoteItem = { ...s };
    for (const f of fields) {
      if (l[f] === undefined) delete (merged as Partial<QuoteItem>)[f];
      else (merged as unknown as Record<string, unknown>)[f] = l[f];
    }
    resolved.set(s.id, merged);
  }
  for (const l of local) {
    if (serverById.has(l.id)) continue;
    const b = baseById.get(l.id);
    // New locally → keep. Deleted on the server: keep only if local edited it.
    if (!b || changedFields(b, l).length > 0) resolved.set(l.id, l);
  }

  const useLocalOrder = reordered(base, local) && !reordered(base, server);
  const primary = useLocalOrder ? local : server;
  const secondary = useLocalOrder ? server : local;

  const order: string[] = primary.map((i) => i.id).filter((id) => resolved.has(id));
  const inPrimary = new Set(order);
  // Index of the last secondary element the primary list also has: whatever
  // the other side appended behind it is a tail addition and goes to the end.
  let lastShared = -1;
  secondary.forEach((it, idx) => { if (inPrimary.has(it.id)) lastShared = idx; });
  let anchor: string | null = null;
  secondary.forEach((it, idx) => {
    if (!resolved.has(it.id)) return;
    if (order.includes(it.id)) { anchor = it.id; return; }
    let pos: number;
    if (idx > lastShared) pos = order.length;
    else if (anchor === null) pos = 0;
    else pos = order.indexOf(anchor) + 1;
    order.splice(pos, 0, it.id);
    anchor = it.id;
  });
  return order.map((id) => resolved.get(id)!);
};

export const mergeQuoteDetails = (
  base: QuoteDetails | undefined,
  local: QuoteDetails | undefined,
  server: QuoteDetails | undefined,
): QuoteDetails => {
  const b = (base || {}) as Record<string, unknown>;
  const l = (local || {}) as Record<string, unknown>;
  const out: Record<string, unknown> = { ...(server || {}) };
  const keys = new Set([...Object.keys(b), ...Object.keys(l)]);
  for (const k of keys) {
    if (k === 'followUpLog') continue;
    if (same(l[k], b[k])) continue; // local did not touch this key
    if (l[k] === undefined) delete out[k];
    else out[k] = l[k];
  }
  const log = unionFollowUpLog(server?.followUpLog, local?.followUpLog);
  if (log.length > 0) out.followUpLog = log;
  return out as QuoteDetails;
};

const DERIVED: (keyof Quote)[] = ['items', 'details', 'netTotal', 'grossTotal'];

/**
 * Patch to write onto `server` so that it carries every change `local` made
 * against `base` and every change the other writer made. Returns `{}` when
 * local changed nothing.
 */
export const mergeQuoteUpdate = (
  base: Quote,
  local: Partial<Quote>,
  server: Quote,
): Partial<Quote> => {
  const patch: Partial<Quote> = {};
  const patchRec = patch as Record<string, unknown>;
  const localRec = local as Record<string, unknown>;
  const baseRec = base as unknown as Record<string, unknown>;
  const serverRec = server as unknown as Record<string, unknown>;

  for (const k of Object.keys(local) as (keyof Quote)[]) {
    if (DERIVED.includes(k)) continue;
    if (localRec[k] === undefined) continue;
    // Changed locally AND not already the server value (then there is nothing to write).
    if (!same(localRec[k], baseRec[k]) && !same(localRec[k], serverRec[k])) patchRec[k] = localRec[k];
  }

  const itemsTouched = local.items !== undefined && !same(local.items, base.items || []);
  const detailsTouched = local.details !== undefined && !same(local.details, base.details || {});

  let details: QuoteDetails | undefined;
  if (detailsTouched) {
    details = mergeQuoteDetails(base.details, local.details, server.details);
    if (!same(details, server.details || {})) patch.details = details;
  }

  const discountChanged = 'discountPercent' in patch;
  const taxChanged = 'taxRate' in patch;
  if (itemsTouched || discountChanged || taxChanged) {
    let items = server.items || [];
    if (itemsTouched) {
      const sections = (details ?? server.details)?.sections ?? DEFAULT_QUOTE_SECTIONS;
      items = computePosNumbers(sections, mergeQuoteItems(base.items || [], local.items || [], items));
      if (!same(items, server.items || [])) patch.items = items;
    }
    const totals = calculateQuoteTotals(
      items,
      patch.discountPercent ?? server.discountPercent ?? 0,
      patch.taxRate ?? server.taxRate ?? 0,
    );
    if (totals.netTotal !== server.netTotal) patch.netTotal = totals.netTotal;
    if (totals.grossTotal !== server.grossTotal) patch.grossTotal = totals.grossTotal;
  }

  return patch;
};
